import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { HttpInterceptorRequest, HttpModule } from '@pichincha/angular-sdk/http'
import { StorageModule } from '@pichincha/angular-sdk/storage'
import { EStorageType } from '@pichincha/angular-sdk/auth'
import { AppRoutingModule } from './app-routing.module'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { TRANSLATE_CONFIG } from '@pichincha/bb-commons/internationalization'
import { setAuthorization } from '@pichincha/bb-commons/interceptor'
import { ExternalAssetsConfig, ExternalAssetsModule } from '@pichincha/angular-sdk/external-assets'
import { defineCustomElements } from '@pichincha/ds-core/loader'
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive' // this includes the core NgIdleModule but includes keepalive providers for easy wireup
import { environment } from '../environments/environment'
import { InternationalizationModule } from './internationalization/internationalization.module'

/**
 * Components
 */
import { AppComponent } from './app.component'
import { HeaderComponent } from './components/header/header.component'
import { FooterComponent } from './components/footer/footer.component'
import { EmptyComponent } from './components/empty/empty.component'
import { LoginLayoutComponent } from './layouts/login-layout/login-layout.component'
import { BrandComponent } from './components/brand/brand.component'
import { FacephiLayoutComponent } from './layouts/facephi-layout/facephi-layout.component'
import { ControlListLayoutComponent } from './layouts/control-list-layout/control-list-layout.component'
import { ModalComponent } from './components/modal/modal.component'
import { StepperComponent } from './components/stepper/stepper.component'
import { FircLayoutComponent } from './layouts/firc-layout/firc-layout.component'
import { FiscalLayoutComponent } from './layouts/fiscal-layout/fiscal-layout.component'
import { PageNotFoundComponent } from './views/page-not-found/page-not-found.component'
import { PageTimeoutComponent } from './views/page-timeout/page-timeout.component'
import { OtpLayoutComponent } from './layouts/otp-layout/otp-layout.component'
import { SuccessLayoutComponent } from './layouts/success-layout/success-layout.component'
import { RedirectPageComponent } from './views/redirect-page/redirect-page.component'

/**
 * Guard
 */
import { LoggedGuard } from './guard/logged/logged.guard'
import { AuthenticatedGuard } from './guard/authenticated/authenticated.guard'
import { UrlValidatedGuard } from './guard/url-validated/url-validated.guard'
import { FormCompletedGuard } from './guard/form-completed/form-completed.guard'
import { InsuranceLayoutComponent } from './layouts/insurance-layout/insurance-layout.component'
import { SimulationLayoutComponent } from './layouts/simulation-layout/simulation-layout.component';
import { DebitCardLayoutComponent } from './layouts/debit-card-layout/debit-card-layout.component'
import { SupportLayoutComponent } from './layouts/support-layout/support-layout.component'
import { PageViewBaseComponent } from './views/page-view-base/page-view-base.component'

const configStorage = {
  storageType: EStorageType.SESSION,
  secretKey: environment.storage.key,
}

const assets: Array<ExternalAssetsConfig> = [
  {
    type: 'javascript',
    url: `https://www.google.com/recaptcha/api.js?render=${environment.google.captchaSiteKey}`,
  },
  {
    type: 'javascript',
    url: `https://www.googletagmanager.com/gtag/js?id=${environment.google.analyticsId}`,
  },
  {
    type: 'javascript',
    url: `https://maps.googleapis.com/maps/api/js?key=${environment.google.mapsKey}&libraries=places`,
  },
]

@NgModule({
  declarations: [
    AppComponent,
    EmptyComponent,
    HeaderComponent,
    FooterComponent,
    LoginLayoutComponent,
    FacephiLayoutComponent,
    FircLayoutComponent,
    ControlListLayoutComponent,
    BrandComponent,
    StepperComponent,
    FiscalLayoutComponent,
    PageNotFoundComponent,
    PageTimeoutComponent,
    OtpLayoutComponent,
    ModalComponent,
    SuccessLayoutComponent,
    InsuranceLayoutComponent,
    RedirectPageComponent,
    SimulationLayoutComponent,
    DebitCardLayoutComponent,
    SupportLayoutComponent,
    PageViewBaseComponent,
  ],
  imports: [
    InternationalizationModule,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    ExternalAssetsModule.forRoot(assets),
    StorageModule.forRoot(configStorage),
    NgIdleKeepaliveModule.forRoot(),
    HttpModule.forRoot({ api_url: 'http://localhost:3000' }),
  ],
  providers: [
    LoggedGuard,
    AuthenticatedGuard,
    UrlValidatedGuard,
    FormCompletedGuard,
    {
      provide: HttpInterceptorRequest,
      useValue: setAuthorization,
    },
    {
      provide: TRANSLATE_CONFIG,
      useValue: {
        availableLanguages: ['es', 'en'],
        defaultLanguage: 'es',
        urlAssets: environment.translationFiles,
      },
    },
    { provide: 'googleTagManagerId', useValue: 'GTM-MKB2ZLD' },
    {
      provide: APP_INITIALIZER,
      useFactory: () => defineCustomElements,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
